import { IManagerProduct } from '@/types';
import { Modal } from '../Modal';
import { BUTTON_LABELS, FEATURE_FLAGS, MANAGER_LABELS } from '@/constants';

// Feature flag for certification locking
const { ENABLE_CERTIFICATION_LOCKING } = FEATURE_FLAGS;

/** Props for the UnlockCertificationModal component */
interface UnlockCertificationModalProps {
  /** The active certification product for the manager */
  activeProduct: IManagerProduct | null;
  /** Optional denial error message shown after failed unlock attempt */
  errorMessage?: string | null;
  /** Whether the unlock modal is open */
  isOpen: boolean;
  /** Callback for when the modal is canceled */
  onCancel: () => void;
  /** Callback for when the modal is confirmed */
  onConfirm: () => void;
  /** The user selected for unlocking */
  userIdToUnlock?: string;
}

/**
 * UnlockCertificationModal Component
 *
 * This component renders a confirmation modal for unlocking a certification for a sales representative.
 *
 * It is only rendered if the certification locking feature is enabled. The modal displays relevant
 * information about the certification being unlocked and provides confirm and cancel actions.
 */
export const UnlockCertificationModal: React.FC<UnlockCertificationModalProps> = ({
  activeProduct,
  errorMessage,
  onCancel,
  onConfirm,
  isOpen,
  userIdToUnlock,
}) => {
  // If the certification locking feature is not enabled, do not render the modal
  if (!ENABLE_CERTIFICATION_LOCKING) {
    return null;
  }

  // Render the unlock confirmation modal with appropriate data attributes and content
  return (
    <Modal
      data-certification={activeProduct?.certificationId}
      data-testid="unlock-confirmation-modal"
      data-user={userIdToUnlock}
      isOpen={isOpen}
      onClose={onCancel}
      onPrimaryAction={onConfirm}
      primaryButtonLabel={BUTTON_LABELS.CONFIRM}
      secondaryButtonLabel={BUTTON_LABELS.CANCEL}
      title={MANAGER_LABELS.UNLOCK_MODAL_TITLE}
    >
      <div className="flex flex-col gap-3">
        <p>{MANAGER_LABELS.UNLOCK_MODAL_DESCRIPTION}</p>
        {errorMessage ? (
          <p className="text-sm font-medium text-red-600" role="alert">
            {errorMessage}
          </p>
        ) : null}
      </div>
    </Modal>
  );
};

UnlockCertificationModal.displayName = 'UnlockCertificationModal';

export default UnlockCertificationModal;
